import React from 'react';
import { Section } from '../types';
import { safetyKeyPoints } from '../utils/safetyKeyPoints';

interface NotesModalProps {
  section: Section;
  isOpen: boolean;
  onClose: () => void;
}

// Helper function to organize safety notes by category (reused from NotesPanel)
const organizeSafetyNotesByCategory = (notes: string[]) => {
  const categorized: { [category: string]: string[] } = {};
  const uncategorized: string[] = [];

  notes.forEach(note => {
    // Find matching key points from our centralized data
    const matchingKeyPoint = safetyKeyPoints.keypoints.find(kp =>
      kp.variations.some(variation =>
        variation.toLowerCase().includes(note.toLowerCase().substring(0, 30)) ||
        note.toLowerCase().includes(variation.toLowerCase().substring(0, 30))
      )
    );

    if (matchingKeyPoint) {
      const category = matchingKeyPoint.key_point.split(':')[0].trim();
      if (!categorized[category]) {
        categorized[category] = [];
      }
      categorized[category].push(note);
    } else {
      uncategorized.push(note);
    }
  });

  return { categorized, uncategorized };
};

const NotesModal: React.FC<NotesModalProps> = ({ section, isOpen, onClose }) => {
  if (!isOpen) return null;

  const { categorized, uncategorized } = organizeSafetyNotesByCategory(section.safetyNotes);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <svg className="w-6 h-6 text-safety-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <h2 className="text-xl font-semibold text-gray-900">Safety Notes</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">{section.name}</h3>
            <p className="text-gray-600 mb-6">{section.description}</p>

            <div className="space-y-5">
              <h4 className="font-medium text-gray-900">Key Safety Points:</h4>

              {/* Categorized Safety Notes */}
              {Object.entries(categorized).map(([category, notes]) => (
                <div key={category} className="space-y-3">
                  <h5 className="font-semibold text-safety-700 text-sm uppercase tracking-wide border-b border-safety-200 pb-1">
                    {category}
                  </h5>
                  <ul className="space-y-2 ml-4">
                    {notes.map((note, index) => (
                      <li key={`${category}-${index}`} className="flex items-start">
                        <svg className="w-4 h-4 text-safety-600 mt-1 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">{note}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {/* Uncategorized Safety Notes */}
              {uncategorized.length > 0 && (
                <div className="space-y-3">
                  <h5 className="font-semibold text-gray-600 text-sm uppercase tracking-wide border-b border-gray-200 pb-1">
                    General Safety
                  </h5>
                  <ul className="space-y-2 ml-4">
                    {uncategorized.map((note, index) => (
                      <li key={`uncategorized-${index}`} className="flex items-start">
                        <svg className="w-4 h-4 text-safety-600 mt-1 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">{note}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <h5 className="text-sm font-medium text-yellow-800">Remember</h5>
                  <p className="text-sm text-yellow-700 mt-1">
                    Review these safety notes carefully. Understanding and following these guidelines is essential for safe operation.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="btn-primary"
            >
              Close and Continue Quiz
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotesModal;